import type { AbortKey } from '@app/adapters/abort'
import type {
  AccessListItem,
  AccessListName,
  AccessListPaging,
  AccessPage,
  AccessRowTypes,
  DraftGroup,
  DraftRole,
  GroupWrite,
  NamedRow,
  RoleKind,
  RoleWrite,
} from '@app/application/user-management/types'

import {
  ACCESS_LISTS,
  DESTROY,
  STALE_ACCESS_LIST_REQUEST_REASON,
} from '@app/application/user-management/constants'
import { abort, getSignal } from '@app/adapters/abort'
import { isDraftRole } from '@app/application/user-management/predicates'

// A role the user just added has no id yet, so the company it points at keys
// the row instead.
export const roleKey = (role: DraftRole): AccessListItem['key'] =>
  role.id ?? `company-${role.companyId}`

export const roleItems = (roles: DraftRole[]): AccessListItem[] =>
  roles.map((role) => ({
    key: roleKey(role),
    title: role.companyName,
    removable: true,
  }))

export type DraftRoleList = 'firmRoles' | 'externalRoles' | 'customerRoles'

export const ROLE_LISTS: DraftRoleList[] = [
  'firmRoles',
  'externalRoles',
  'customerRoles',
]

export type AccessRow = DraftRole | NamedRow

// A list holds the rows loaded from the server so far, and the rows added and
// removed in the panel since. Only the added and removed rows reach the save.
export interface AccessListState<T extends AccessRow> {
  rows: T[]
  added: T[]
  removed: T[]
  query: string
  page: number
  totalPages: number
  // The server's count for `query`, and for the whole list.
  matchCount: number
  count: number
  loading: boolean
  loadingMore: boolean
  loaded: boolean
  failed: boolean
}

export type AccessLists = {
  [L in AccessListName]: AccessListState<AccessRowTypes[L]>
}

// The added rows come first, so a new row shows without paging.
export interface AccessListView<T extends AccessRow> extends AccessListPaging {
  rows: T[]
}

export type AccessListViews = {
  [L in AccessListName]: AccessListView<AccessRowTypes[L]>
}

const rowKey = (row: AccessRow): AccessListItem['key'] =>
  isDraftRole(row) ? roleKey(row) : row.id

const rowTitle = (row: AccessRow): string =>
  isDraftRole(row) ? row.companyName : row.name

// The same match as the server's `ILIKE`, for the rows it does not hold yet.
const matches = (row: AccessRow, query: string): boolean =>
  rowTitle(row).toLowerCase().includes(query.trim().toLowerCase())

// A record being created has nothing saved, so its lists start complete.
export const emptyList = <T extends AccessRow>(
  saved: boolean
): AccessListState<T> => ({
  rows: [],
  added: [],
  removed: [],
  query: '',
  page: 0,
  totalPages: 0,
  matchCount: 0,
  count: 0,
  loading: false,
  loadingMore: false,
  loaded: !saved,
  failed: false,
})

export const ACCESS_LIST_NAMES = Object.keys(ACCESS_LISTS) as AccessListName[]

// One entry per access list. `Object.fromEntries` drops the type each list
// has, so the result is cast back. Each caller builds an entry from that list's
// own state, which keeps the cast true.
const perList = <R>(build: (name: AccessListName) => unknown): R =>
  Object.fromEntries(ACCESS_LIST_NAMES.map((name) => [name, build(name)])) as R

export const emptyLists = (saved: boolean): AccessLists =>
  perList(() => emptyList(saved))

// A list's label holds its count, which is unknown until the first page lands.
export const accessCount = (view: AccessListPaging): number | string =>
  view.loaded ? view.count : '…'

export const viewOf = <T extends AccessRow>(
  list: AccessListState<T>
): AccessListView<T> => {
  const removedKeys = new Set(list.removed.map(rowKey))
  const matching = (rows: T[]): T[] =>
    rows.filter((row) => matches(row, list.query))

  return {
    rows: [
      ...matching(list.added),
      ...list.rows.filter((row) => !removedKeys.has(rowKey(row))),
    ],
    count: list.count + list.added.length - list.removed.length,
    matchCount:
      list.matchCount +
      matching(list.added).length -
      matching(list.removed).length,
    hasMore: list.page < list.totalPages,
    loading: list.loading,
    loadingMore: list.loadingMore,
    loaded: list.loaded,
    failed: list.failed,
    query: list.query,
  }
}

export const viewsOf = (lists: AccessLists): AccessListViews =>
  perList((name) => viewOf<AccessRow>(lists[name]))

export const removeRow = <T extends AccessRow>(
  list: AccessListState<T>,
  key: AccessListItem['key']
): void => {
  const addedIndex = list.added.findIndex((row) => rowKey(row) === key)

  if (addedIndex >= 0) {
    list.added.splice(addedIndex, 1)

    return
  }

  const row = list.rows.find((candidate) => rowKey(candidate) === key)

  if (row) list.removed.push(row)
}

export const roleWrites = (
  kind: RoleKind,
  list: AccessListState<DraftRole>
): RoleWrite[] => [
  ...list.added.map((role) => ({ companyId: role.companyId, role: kind })),
  ...list.removed.map((role) => ({
    id: role.id as number,
    role: kind,
    _destroy: DESTROY,
  })),
]

export const groupWrites = (
  list: AccessListState<DraftGroup>
): GroupWrite[] => [
  ...list.added.map((group) => ({ id: group.id })),
  ...list.removed.map((group) => ({ id: group.id, _destroy: DESTROY })),
]

// Loads one page of `list` for `query`. A newer call on the same `requestKey`
// cancels this one. Without `fetchPage` the list only takes the query, for a
// record that has nothing saved to page through.
export async function loadAccessPage<T extends AccessRow>(
  list: AccessListState<T> | undefined,
  requestKey: AbortKey,
  query: string,
  page: number,
  fetchPage: ((signal?: AbortSignal) => Promise<AccessPage<T> | null>) | null
): Promise<void> {
  abort(requestKey, STALE_ACCESS_LIST_REQUEST_REASON)

  if (!list) return

  list.query = query

  if (!fetchPage) return

  const signal = getSignal(requestKey)
  const isStale = (): boolean => signal?.aborted === true

  list.loading = true
  list.loadingMore = page > 1
  list.failed = false

  try {
    const response = await fetchPage(signal)

    if (isStale()) return

    if (response) {
      const rows = response.items.map((item) => ({ ...item }))

      list.rows = page === 1 ? rows : [...list.rows, ...rows]
      list.page = response.pagination.currentPage
      list.totalPages = response.pagination.totalPages
      list.matchCount = response.pagination.totalCount
      if (query.trim() === '') list.count = response.pagination.totalCount
      list.loaded = true
    }
  } catch {
    if (isStale()) return

    list.failed = true

    // The loaded rows answer an older query, so the next page would append
    // rows of the new query to them.
    if (page === 1) {
      list.rows = []
      list.page = 0
      list.totalPages = 0
    }
  }

  list.loading = false
  list.loadingMore = false
}
